/**
 * 計画マスタ Codes 4-button 編集 UI。
 *
 * 発電販売計画マスタ (GenerationSalesPlanMaster) と
 * 需要調達計画マスタ (DemandProcurementPlanMaster) の両 Edit モーダルで共有する。
 *
 * 4 ボタンの挙動は mock-app OcctoMaster.razor 1443-1505 行・cs 515-571 行に準拠:
 *   ＋スポット: code=JSPT3, codeName=JEPX スポット, isInternal=false (制度固定、既登録なら disabled)
 *   ＋時間前:   code=J1HR3, codeName=JEPX 時間前, isInternal=false (制度固定)
 *   ※ 自社エリアが沖縄 (JEPX 対象外) の時は ＋スポット / ＋時間前を出さない (AC-OMC-014 / AC-OMG-013)
 *   ＋自社:     isInternal=true, code ドロップダウン (BgMaster + SubmitterMaster - 自身キー - 既登録)
 *   ＋他社:     isInternal=false, code ドロップダウン (取引先マスタ accounts)
 *
 * D4: codes 編集 UI 追加 (proto 追従)。
 */
import { useMemo, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { TradingPartnerListItem } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import {
  type Control,
  Controller,
  type FieldValues,
  useController,
  useFieldArray,
  useFormState,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  FieldRoot,
  HStack,
  Stack,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular } from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { DsSelect } from '~/components/common/DsSelect'
import { FieldErrorText } from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import { iconSize } from '~/constants/iconSize'
import { isJepxArea, isSameCounterpartyAreaGroup } from '~/models/area'
import { type CivilDate, todayCivilDate } from '~/models/civilDate'
import { isActiveOn } from '~/models/validPeriod'
import type { BgMasterRow } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import type { SubmitterMasterRow } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useListSubmitterMasters'
import { ImmutableInput } from '~/pages/AdminOcctoMaster/tabs/_common/components/ImmutableInput'
import { PeriodFields } from '~/pages/AdminOcctoMaster/tabs/_common/components/PeriodFields'
import { SubFormLabel } from '~/pages/AdminOcctoMaster/tabs/_common/components/SubFormLabel'
import {
  planMasterRowDefaultValidFrom,
  resolveExpiryFallbackLabel,
} from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

type CodeEntry = {
  code: string
  codeName: string
  isInternal: boolean
  validFrom: { year: number; month: number; day: number }
  validTo?: { year: number; month: number; day: number }
}

type Props = {
  // Control<FieldValues> is the widest type that accepts any form schema while
  // keeping RHF's inference intact. We erase the specific schema type here
  // because this component is shared between two different form schemas
  // (GenerationSalesMasterFormValues and DemandProcurementMasterFormValues).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<FieldValues, any, FieldValues>
  /**
   * 自身のキー (= 自社コードオプションから除外する submitterCode / bgCode)。
   * 指定なしの場合は除外スキップ。
   */
  selfKey?: string
  /**
   * 計画提出者マスタ一覧 (自社コード候補の一部として使用)。 list クエリ
   * 取得中は undefined (fallback 解決の「(削除済み)」誤表示防止、
   * PR #5920 レビュー指摘)。
   */
  submitters: SubmitterMasterRow[] | undefined
  /**
   * BG マスタ一覧 (自社コード候補の一部として使用)。 list クエリ取得中は
   * undefined (fallback 解決の「(削除済み)」誤表示防止、PR #5920 レビュー指摘)。
   */
  bgMasters: BgMasterRow[] | undefined
  /** 取引先マスタ一覧 (他社コード候補として使用) */
  tradingPartners: TradingPartnerListItem[]
  /**
   * 自身のエリアコード。指定された場合、他社コード候補を同エリアの account のみに絞り込み、
   * JEPX 対象外のエリア (沖縄) では ＋スポット / ＋時間前を出さない。
   * 未指定の場合は全 account を候補とする。 JEPX ボタンは selfKey も未指定なら出し、
   * selfKey があるのに未指定 (= エリアが未解決: マスタの取得中・取得失敗・該当なし) なら
   * 沖縄かどうかが判らないため出さない (AC-OMC-014 / AC-OMG-013)。
   */
  selfAreaCode?: AreaCode
  /** i18n ページキー namespace (= 'adminOcctoMaster.generationSalesPlanMaster' 等) */
  i18nNs:
    | 'adminOcctoMaster.generationSalesPlanMaster'
    | 'adminOcctoMaster.demandProcurementPlanMaster'
  /** 送信者キーが未設定で +ボタン disabled 時に表示する tooltip 文言の i18n key */
  selfKeyMissingI18nKey: string
  /**
   * codes[] 配列全体の validation エラーメッセージ (AC-01、例:
   * 「取引先を1件以上選択してください」)。 呼び出し側が
   * `errors.codes?.root?.message` を解決して渡す (field-level 表示、
   * top-level alert は呼び出し側の `root.serverError` が別途担う)。
   */
  codesErrorMessage?: string
  /**
   * 他社 (isInternal=false かつ 固定 JEPX 以外) の code select ラベルを
   * 上書きする i18n key。未指定の場合は `${i18nNs}.form.codeLabel` を使う。
   * 需要調達計画マスタで「取引先」表記に切り替える用途。
   */
  externalCodeLabelI18nKey?: string
  /**
   * codeName が auto-fill / 固定値で決まるエントリ (JEPX スポット / JEPX 時間前 /
   * 他社) の 取引先コード名称 入力欄を非表示にする。 内訳:
   * - JEPX 固定 (JSPT3 / J1HR3): codeName は append 時に `JEPX スポット` /
   *   `JEPX 時間前` で固定される
   * - 他社: 選択された account の code_name
   *   (= proto `TradingPartnerListItemAccount.code_name`) で自動補完される
   * dropdown / 固定値で情報が既に見えているため 取引先コード名称 入力欄が冗長になる
   * ケース向け (需要調達計画マスタ)。 自社 (isInternal=true) は user 入力が
   * 必要なため対象外。
   */
  hideAutoFilledCodeName?: boolean
}

export const PlanMasterCodesSection = ({
  control,
  selfKey,
  submitters,
  bgMasters,
  tradingPartners,
  selfAreaCode,
  i18nNs,
  selfKeyMissingI18nKey,
  codesErrorMessage,
  externalCodeLabelI18nKey,
  hideAutoFilledCodeName = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'codes',
  })

  // useWatch で codes[] の最新値を取得 (useFieldArray の fields は re-render で stale になる)
  const rawWatchedCodes = useWatch({ control, name: 'codes' }) as
    | CodeEntry[]
    | undefined
  // rawWatchedCodes は毎回新しい参照になるため、undefined フォールバックを外で行い
  // registeredCodes の deps を安定させる
  const watchedCodes = rawWatchedCodes ?? []

  // 自社行の code select は FormSelect が内部で Controller を張るため、 error 表示用の
  // fieldState はここで一括購読する (行ごとに Controller を二重に張らない。
  // PlanMasterBgsSection と同じ形)。
  const { errors } = useFormState({ control })
  const codeErrors = errors.codes as
    | Array<{ code?: { message?: string } } | undefined>
    | undefined

  const keyMissing = selfKey === undefined || selfKey === ''

  // form 全体の validFrom (= レコードの適用開始日) を購読する (AC-MOI-023、
  // occto-master-fk-as-of-filter 設計書 Decision 1、Slice 9 と同じ購読パターン)。
  const watchedRecordValidFrom = useWatch({ control, name: 'validFrom' }) as
    | CivilDate
    | undefined
  const defaultValidFrom = (): CivilDate =>
    planMasterRowDefaultValidFrom(watchedRecordValidFrom)

  /** 登録済みコードのセット (重複追加防止) */
  const registeredCodes = useMemo<Set<string>>(
    () =>
      new Set(
        (rawWatchedCodes ?? []).map((c) => c.code).filter((s) => s !== ''),
      ),
    [rawWatchedCodes],
  )

  /** JEPX スポット既登録チェック */
  const hasSpot = registeredCodes.has('JSPT3')
  /** JEPX 時間前既登録チェック */
  const hasIntraday = registeredCodes.has('J1HR3')
  // AC-OMC-014 / AC-OMG-013: 沖縄は JEPX の取引対象外のため、 JEPX 追加ボタン自体を出さない。
  // 代表 BG を後から沖縄に変えた時の既存 JEPX 行は spec に定めが無く、 削除せず表示を保つ。
  // BG / 提出者が未選択 (keyMissing) の間は従来どおり出す。 選択済みでエリアが未解決
  // (selfAreaCode が undefined) の間は、 沖縄の誤追加を防ぐため出さない。
  const canAddJepx =
    keyMissing ||
    (selfAreaCode !== undefined &&
      (selfAreaCode === AreaCode.UNSPECIFIED || isJepxArea(selfAreaCode)))

  // 自社コード fallback 解決用の絞込前一覧 (BgMaster + SubmitterMaster を
  // 共通の { code, label, validFrom, validTo } 形状へ正規化)。 selfKey /
  // registeredCodes / as-of による絞り込みは行わない (= fallback は選択済みの値の
  // 実在と、 行の適用開始日時点の終了・適用前の判定専用)。
  // bgMasters / submitters のいずれかが list クエリ取得中 (undefined) の間は
  // undefined を返し、 実在参照が一瞬「(削除済み)」に誤表示されるのを防ぐ
  // (PR #5920 レビュー指摘)。
  const allInternalRefs = useMemo(():
    | Array<{
        code: string
        label: string
        validFrom: CivilDate
        validTo: CivilDate | undefined
      }>
    | undefined => {
    if (bgMasters === undefined || submitters === undefined) return undefined
    return [
      ...bgMasters.map((bg) => ({
        code: bg.bgCode,
        label: `${bg.bgCode} - ${bg.bgName}`,
        validFrom: bg.validFrom,
        validTo: bg.validTo,
      })),
      ...submitters.map((s) => ({
        code: s.submitterCode,
        label: `${s.submitterCode} - ${s.submitterName}`,
        validFrom: s.validFrom,
        validTo: s.validTo,
      })),
    ]
  }, [bgMasters, submitters])

  /**
   * 自社コード select の候補外 (as-of narrow 後) fallback ラベル
   * (AC-MOI-023、occto-master-fk-as-of-filter 設計書 Decision 2)。
   */
  const internalFallbackLabel = (
    code: string,
    asOf: CivilDate | undefined,
  ): string | undefined =>
    resolveExpiryFallbackLabel(allInternalRefs, code, asOf, {
      idOf: (r) => r.code,
      labelOf: (r) => r.label,
      validTo: (r) => r.validTo,
      deletedLabel: t('page:adminOcctoMaster.common.masterFallback.deleted'),
      expiredSuffix: t(
        'page:adminOcctoMaster.common.masterFallback.expiredSuffix',
      ),
      future: {
        validFrom: (r) => r.validFrom,
        suffix: t('page:adminOcctoMaster.common.masterFallback.futureSuffix'),
      },
    })

  /**
   * 自社コード候補 (BgMaster + SubmitterMaster から自身コード・使用済みコード・
   * asOf 時点で有効でないものを除外、AC-MOI-023)。 行の select は行の適用開始日、
   * 追加ボタンの可否と「残っていません」注記は追加される行の初期日付
   * (defaultValidFrom) を asOf に渡して同じ関数を呼ぶ。 keepCode は行の既選択値で、
   * 使用済みコードに含まれていても候補に残す。
   */
  const internalCandidates = (
    asOf: CivilDate,
    usedCodes: ReadonlySet<string>,
    keepCode?: string,
  ): Array<{ code: string; label: string }> => [
    ...(bgMasters ?? [])
      .filter(
        (bg) =>
          bg.bgCode !== selfKey &&
          (!usedCodes.has(bg.bgCode) || bg.bgCode === keepCode) &&
          isActiveOn(bg.validFrom, bg.validTo, asOf),
      )
      .map((bg) => ({
        code: bg.bgCode,
        label: `${bg.bgCode} - ${bg.bgName}`,
      })),
    ...(submitters ?? [])
      .filter(
        (s) =>
          s.submitterCode !== selfKey &&
          (!usedCodes.has(s.submitterCode) || s.submitterCode === keepCode) &&
          isActiveOn(s.validFrom, s.validTo, asOf),
      )
      .map((s) => ({
        code: s.submitterCode,
        label: `${s.submitterCode} - ${s.submitterName}`,
      })),
  ]

  /** 追加される新しい行の初期日付時点の候補 (追加ボタンの可否・注記用) */
  const internalOptions = internalCandidates(
    defaultValidFrom(),
    registeredCodes,
  )

  /**
   * 他社コードオプション (取引先マスタ accounts から沖縄連系グループ絞り込み・
   * 既登録除外)。「登録できる取引先が残っていません」inline note (AC-06) の
   * 判定に使う。
   */
  const externalOptions = useMemo<Array<{ code: string }>>(
    () =>
      tradingPartners.flatMap((tp) =>
        tp.accounts
          .filter(
            (acc) =>
              (selfAreaCode === undefined ||
                isSameCounterpartyAreaGroup(selfAreaCode, acc.areaCode)) &&
              !registeredCodes.has(acc.code),
          )
          .map((acc) => ({ code: acc.code })),
      ),
    [tradingPartners, selfAreaCode, registeredCodes],
  )

  const SPOT_TITLE = keyMissing
    ? t(`page:${selfKeyMissingI18nKey}`)
    : hasSpot
      ? t(`page:${i18nNs}.form.codeAlreadyRegistered`)
      : ''

  const INTRADAY_TITLE = keyMissing
    ? t(`page:${selfKeyMissingI18nKey}`)
    : hasIntraday
      ? t(`page:${i18nNs}.form.codeAlreadyRegistered`)
      : ''

  const INTERNAL_TITLE = keyMissing
    ? t(`page:${selfKeyMissingI18nKey}`)
    : internalOptions.length === 0
      ? t(`page:${i18nNs}.form.noInternalOptions`)
      : ''

  const EXTERNAL_TITLE = keyMissing
    ? t(`page:${selfKeyMissingI18nKey}`)
    : externalOptions.length === 0
      ? t(`page:${i18nNs}.form.noExternalOptions`)
      : ''

  return (
    <Box>
      {/* セクションヘッダー: ラベル + 4 ボタン (JEPX 2 ボタンは canAddJepx の時のみ。 design `.occto-sub-header` 準拠:
          border-top + padding-top + marginTop で直前セクションと区切り、
          add ボタンは size="sm" で他マスタと統一)。 */}
      <HStack
        align="center"
        justify="space-between"
        marginTop={4}
        marginBottom={2}
        paddingTop={3}
        borderTopWidth="1px"
        borderTopColor="Border.lowEmphasis"
      >
        <Text textStyle="modalSectionTitle" color="Text.highEmphasis">
          {t(`page:${i18nNs}.form.codes`)}
        </Text>
        <HStack gap={2}>
          {canAddJepx && (
            <>
              <ButtonTooltip content={SPOT_TITLE}>
                <TertiaryButton
                  disabled={keyMissing || hasSpot}
                  onClick={() => {
                    void append({
                      code: 'JSPT3',
                      codeName: 'JEPX スポット',
                      isInternal: false,
                      validFrom: defaultValidFrom(),
                      validTo: undefined,
                    })
                  }}
                  size="sm"
                  title={SPOT_TITLE}
                >
                  {/* 追加ボタンの ＋ は DS icon で描く。 従来は i18n 文言に全角の `＋ ` を
                      埋めていたためフォント依存で、 同じ役割の「入札追加」 (icon) と質感が
                      揃わず、 翻訳のたびに記号を書き写す必要もあった。
                      design css `.btn:not(...)>svg` = 20px。 */}
                  <FaPlusRegular boxSize={iconSize.labeledButton} />
                  {t(`page:${i18nNs}.form.addSpot`)}
                </TertiaryButton>
              </ButtonTooltip>
              <ButtonTooltip content={INTRADAY_TITLE}>
                <TertiaryButton
                  disabled={keyMissing || hasIntraday}
                  onClick={() => {
                    void append({
                      code: 'J1HR3',
                      codeName: 'JEPX 時間前',
                      isInternal: false,
                      validFrom: defaultValidFrom(),
                      validTo: undefined,
                    })
                  }}
                  size="sm"
                  title={INTRADAY_TITLE}
                >
                  {/* 追加ボタンの ＋ は DS icon で描く。 従来は i18n 文言に全角の `＋ ` を
                      埋めていたためフォント依存で、 同じ役割の「入札追加」 (icon) と質感が
                      揃わず、 翻訳のたびに記号を書き写す必要もあった。
                      design css `.btn:not(...)>svg` = 20px。 */}
                  <FaPlusRegular boxSize={iconSize.labeledButton} />
                  {t(`page:${i18nNs}.form.addIntraday`)}
                </TertiaryButton>
              </ButtonTooltip>
            </>
          )}
          <ButtonTooltip content={INTERNAL_TITLE}>
            <TertiaryButton
              disabled={keyMissing || internalOptions.length === 0}
              onClick={() => {
                void append({
                  code: '',
                  codeName: '',
                  isInternal: true,
                  validFrom: defaultValidFrom(),
                  validTo: undefined,
                })
              }}
              size="sm"
              title={INTERNAL_TITLE}
            >
              {/* 追加ボタンの ＋ は DS icon で描く。 従来は i18n 文言に全角の `＋ ` を
                  埋めていたためフォント依存で、 同じ役割の「入札追加」 (icon) と質感が
                  揃わず、 翻訳のたびに記号を書き写す必要もあった。
                  design css `.btn:not(...)>svg` = 20px。 */}
              <FaPlusRegular boxSize={iconSize.labeledButton} />
              {t(`page:${i18nNs}.form.addInternal`)}
            </TertiaryButton>
          </ButtonTooltip>
          <ButtonTooltip content={EXTERNAL_TITLE}>
            <TertiaryButton
              disabled={keyMissing || externalOptions.length === 0}
              onClick={() => {
                void append({
                  code: '',
                  codeName: '',
                  isInternal: false,
                  validFrom: defaultValidFrom(),
                  validTo: undefined,
                })
              }}
              size="sm"
              title={EXTERNAL_TITLE}
            >
              {/* 追加ボタンの ＋ は DS icon で描く。 従来は i18n 文言に全角の `＋ ` を
                  埋めていたためフォント依存で、 同じ役割の「入札追加」 (icon) と質感が
                  揃わず、 翻訳のたびに記号を書き写す必要もあった。
                  design css `.btn:not(...)>svg` = 20px。 */}
              <FaPlusRegular boxSize={iconSize.labeledButton} />
              {t(`page:${i18nNs}.form.addExternal`)}
            </TertiaryButton>
          </ButtonTooltip>
        </HStack>
      </HStack>

      {/* disabled 注記 (AC-06): ホバー tooltip でなく常時可視の縦積み。
          keyMissing 時は選択を促す note のみ (他条件は無関係)、
          それ以外は該当する条件すべてを並置する。 */}
      <VStack align="stretch" gap={1} marginBottom={2}>
        {keyMissing ? (
          <Text textStyle="fieldHint" color="Text.midEmphasis">
            {t(`page:${selfKeyMissingI18nKey}`)}
          </Text>
        ) : (
          <>
            {canAddJepx && (hasSpot || hasIntraday) && (
              <Text textStyle="fieldHint" color="Text.midEmphasis">
                {t(`page:${i18nNs}.form.codeAlreadyRegistered`)}
              </Text>
            )}
            {internalOptions.length === 0 && (
              <Text textStyle="fieldHint" color="Text.midEmphasis">
                {t(`page:${i18nNs}.form.noInternalOptions`)}
              </Text>
            )}
            {externalOptions.length === 0 && (
              <Text textStyle="fieldHint" color="Text.midEmphasis">
                {t(`page:${i18nNs}.form.noExternalOptions`)}
              </Text>
            )}
          </>
        )}
      </VStack>

      {codesErrorMessage !== undefined && (
        <FieldRoot marginBottom={2} invalid>
          <FieldErrorText>{codesErrorMessage}</FieldErrorText>
        </FieldRoot>
      )}

      {/* コードエントリ一覧 */}
      <VStack align="stretch" gap={2}>
        {fields.map((field, idx) => {
          const entry: CodeEntry = watchedCodes[idx] ?? {
            code: '',
            codeName: '',
            isInternal: false,
            validFrom: todayCivilDate(),
          }
          // 行ごとの対象日 (AC-MOI-023): 行の適用開始日。
          // 行の適用開始日は入力欄のクリアが無視される (PeriodFields) ため常に
          // 値を持つ。
          const rowAsOf: CivilDate = entry.validFrom
          const isFixedJepxCode =
            entry.code === 'JSPT3' || entry.code === 'J1HR3'
          const isInternal = entry.isInternal

          /**
           * この行の code を除いた使用済みコードセット。
           * 自社・他社ドロップダウンで「既選択の code がリストに残る」を保証するため
           * 行ごとに都度計算する。
           */
          const usedCodesExcludingThisRow = new Set(
            watchedCodes
              .filter((_, i) => i !== idx)
              .map((c) => c.code)
              .filter((s) => s !== ''),
          )

          /**
           * この行向けの自社コードオプション (既選択 code をリストに残す、
           * AC-MOI-023: 行の適用開始日時点で有効でないコードは除外)。
           */
          const internalOptsForRow = internalCandidates(
            rowAsOf,
            usedCodesExcludingThisRow,
            entry.code,
          )

          /**
           * この行向けの他社コードオプション (selfAreaCode 指定時は沖縄連系グループで絞り込み)。
           * `partnerId` / `accountCodeName` は同一 code を持つ別取引先を disambiguate
           * するため option に持たせる (DB `trading_partner_account` は
           * `(organization_id, partner_id, area_cd)` のみが unique で、 同一エリアに
           * 別取引先が同じ code を持つことが許容されるため、 code 単位の find では
           * 選択された account を一意に解決できない)。
           */
          const externalOptsForRow = tradingPartners.flatMap((tp) =>
            tp.accounts
              .filter(
                (acc) =>
                  (selfAreaCode === undefined ||
                    isSameCounterpartyAreaGroup(selfAreaCode, acc.areaCode)) &&
                  (!usedCodesExcludingThisRow.has(acc.code) ||
                    acc.code === entry.code),
              )
              .map((acc) => ({
                code: acc.code,
                label: `${acc.code} - ${tp.name}`,
                partnerId: tp.id,
                accountCodeName: acc.codeName,
              })),
          )

          /** 自社行の code select の error (FormSelect は fieldState を返さないため一括購読分から引く)。 */
          const internalCodeError = codeErrors?.[idx]?.code

          return (
            <Box
              key={field.id}
              padding={3}
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="md"
            >
              <Stack gap={3}>
                {/* コード入力 + 取引先コード名称 + 削除ボタン */}
                <HStack align="flex-end" gap={3}>
                  {/* コード */}
                  <Box flex={1}>
                    {isFixedJepxCode ? (
                      /* JSPT3 / J1HR3: 固定、変更不可 */
                      <FieldRoot>
                        <SubFormLabel>
                          {t(`page:${i18nNs}.form.codeLabel`)}
                        </SubFormLabel>
                        <ImmutableInput readOnly value={entry.code} />
                      </FieldRoot>
                    ) : isInternal ? (
                      /* 自社: BgMaster + SubmitterMaster からドロップダウン */
                      <FieldRoot invalid={internalCodeError !== undefined}>
                        <SubFormLabel>
                          {t(`page:${i18nNs}.form.codeLabel`)}
                        </SubFormLabel>
                        <FormSelect
                          aria-label={t(`page:${i18nNs}.form.codeLabel`)}
                          control={control}
                          /* 既選択値が opts に無い場合のフォールバック表示。
                             対象日 (行の適用開始日) 時点で終了していれば
                             「（終了）」、 まだ適用前なら「（適用前）」の
                             サフィックス付き、 物理削除済みなら「(削除済み)」を
                             表示する
                             (AC-MOI-023、occto-master-fk-as-of-filter 設計書
                             Decision 2)。 DS Select は閉じた状態の表示だけを
                             担わせる (再選択させない。 DsSelect の JSDoc
                             「既存値のフォールバック」)。 */
                          fallbackLabel={(v) =>
                            internalFallbackLabel(v, rowAsOf)
                          }
                          name={`codes.${idx}.code`}
                          options={internalOptsForRow.map((o) => ({
                            value: o.code,
                            label: o.label,
                          }))}
                          placeholder={t(
                            `page:${i18nNs}.form.internalCodeUnselected`,
                          )}
                        />
                        {internalCodeError !== undefined && (
                          <FieldErrorText>
                            {internalCodeError.message}
                          </FieldErrorText>
                        )}
                      </FieldRoot>
                    ) : (
                      /* 他社: 取引先マスタ accounts からドロップダウン */
                      <ExternalCodeSelect
                        control={control}
                        externalCodeLabelI18nKey={externalCodeLabelI18nKey}
                        externalOptsForRow={externalOptsForRow}
                        hideAutoFilledCodeName={hideAutoFilledCodeName}
                        i18nNs={i18nNs}
                        idx={idx}
                      />
                    )}
                  </Box>

                  {/* 取引先コード名称: hideAutoFilledCodeName かつ 非 internal
                      (= JEPX 固定 / 他社) の場合は非表示。
                      JEPX 固定は append 時に固定値、 他社は
                      ExternalCodeSelect の onChange で account.code_name
                      から自動補完されるため 取引先コード名称 入力欄は冗長になる。
                      自社 (isInternal=true) は user 入力が必要なため表示。 */}
                  {!(hideAutoFilledCodeName && !isInternal) && (
                    <Box flex={1}>
                      <Controller
                        control={control}
                        name={`codes.${idx}.codeName`}
                        render={({ field: f, fieldState }) => (
                          <FieldRoot invalid={fieldState.error !== undefined}>
                            <SubFormLabel>
                              {t(`page:${i18nNs}.form.codeNameLabel`)}
                            </SubFormLabel>
                            <ImmutableInput
                              ref={f.ref}
                              onBlur={f.onBlur}
                              onChange={f.onChange}
                              readOnly={isFixedJepxCode}
                              value={f.value as string}
                            />
                            {fieldState.error !== undefined && (
                              <FieldErrorText>
                                {fieldState.error.message}
                              </FieldErrorText>
                            )}
                          </FieldRoot>
                        )}
                      />
                    </Box>
                  )}

                  {/* 削除ボタン */}
                  <TertiaryButton
                    flexShrink={0}
                    aria-label={t(`page:${i18nNs}.form.removeCode`)}
                    colorPalette="red"
                    onClick={() => {
                      remove(idx)
                    }}
                    size="sm"
                  >
                    {t(`page:${i18nNs}.form.removeCode`)}
                  </TertiaryButton>
                </HStack>

                {/* 適用期間 */}
                <PeriodFields
                  basePath={`codes.${idx}`}
                  clearAsNull
                  control={control}
                  i18nNs={i18nNs}
                  spacerWidth="80px"
                />
              </Stack>
            </Box>
          )
        })}
      </VStack>
    </Box>
  )
}

type ExternalCodeSelectProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<FieldValues, any, FieldValues>
  idx: number
  externalOptsForRow: Array<{
    code: string
    label: string
    partnerId: string
    accountCodeName: string
  }>
  i18nNs:
    | 'adminOcctoMaster.generationSalesPlanMaster'
    | 'adminOcctoMaster.demandProcurementPlanMaster'
  externalCodeLabelI18nKey: string | undefined
  hideAutoFilledCodeName: boolean
}

/**
 * 他社行の code select。 hideAutoFilledCodeName が true の時は、 選択された
 * 取引先 account の `codeName` (= proto `TradingPartnerListItemAccount.code_name`、
 * = per-account 取引先コード名称) を form の codeName field に自動補完する。
 *
 * DB `trading_partner_account` の unique index は
 * `(organization_id, partner_id, area_cd)` のみで、 同一エリアに別取引先が同じ
 * code を持つことが許容される。 form 側に持つ値 (code) だけでは選択された
 * account を一意に復元できず、 controlled select が `value={code}` から先頭の
 * 同値 option を選んで表示してしまうため、 option value を
 * `${partnerId}::${code}` の composite にして local state
 * (`selectedPartnerId`) で選択状態を一意に保持する。 form の `code` は変えず、
 * form contract を維持する。
 *
 * codeName の form 反映は `useController` 経由の onChange で行い、
 * `useFieldArray.update` (行全体を replace = field id 再採番による
 * unmount / remount) を避けて 入力中の focus / date picker 状態を保つ。
 *
 * **`FormSelect` ではなく `DsSelect` を使う理由**: select に表示する値は form 値
 * (`code`) ではなく composite (`partnerId::code`) で、 `FormSelect` の
 * 「field.value をそのまま value にする」形では duplicate code を区別できない。
 * `DsSelect` の value / onChange 制御で composite を扱い、 form へは
 * `useController` 経由で code / codeName だけを書き戻す。
 *
 * codeName の解決は native 時代の `data-code-name` (option 要素の dataset) から
 * **`externalOptsForRow` を composite value で引き直す**形に変えた
 * (DS `Select` の option は DOM 属性を持てないため)。 引く鍵は composite なので
 * duplicate code でも選択された account を一意に解決できる (= 挙動は同一)。
 */
const ExternalCodeSelect = ({
  control,
  idx,
  externalOptsForRow,
  i18nNs,
  externalCodeLabelI18nKey,
  hideAutoFilledCodeName,
}: ExternalCodeSelectProps): React.ReactElement => {
  const { t } = useTranslation()
  const { field: codeField, fieldState } = useController({
    control,
    name: `codes.${idx}.code`,
  })
  const { field: codeNameField } = useController({
    control,
    name: `codes.${idx}.codeName`,
  })

  // 選択された取引先識別子。 form には code しか無いため、 同一 code を持つ別
  // 取引先を UI 上で区別するために local state で保持する。 edit flow (form の
  // code だけが与えられる) や外部変更に追随するため、 selectedPartnerId が
  // 現在の code に該当しない時は displayValue 側で自動的に fallback する。
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(
    null,
  )

  const currentCode = codeField.value as string

  /** option の value (= 選択された account を一意に指す composite)。 */
  const compositeValue = (o: { partnerId: string; code: string }): string =>
    `${o.partnerId}::${o.code}`

  // select の value に渡す composite 値を組み立てる。 selectedPartnerId が
  // 現在の code に対して有効なら partner を反映、 そうでなければ code の先頭
  // 一致 option の composite (best-effort、 edit flow の 初期表示)、
  // どちらも該当しなければ raw code (未選択 / fallback 表示用)。
  const displayValue = ((): string => {
    if (currentCode === '') return ''
    if (selectedPartnerId !== null) {
      const pinned = externalOptsForRow.find(
        (o) => o.partnerId === selectedPartnerId && o.code === currentCode,
      )
      if (pinned !== undefined) return compositeValue(pinned)
    }
    const firstMatch = externalOptsForRow.find((o) => o.code === currentCode)
    return firstMatch !== undefined ? compositeValue(firstMatch) : currentCode
  })()

  return (
    <FieldRoot invalid={fieldState.error !== undefined}>
      <SubFormLabel>
        {t(`page:${externalCodeLabelI18nKey ?? `${i18nNs}.form.codeLabel`}`)}
      </SubFormLabel>
      <DsSelect
        aria-label={t(
          `page:${externalCodeLabelI18nKey ?? `${i18nNs}.form.codeLabel`}`,
        )}
        /* 取引先マスタに無い既存 code は「表示だけ」に留める (menu には出さない)。
           displayValue は該当 option が無いとき raw code を返すので、 その値を
           そのまま文言に埋める。 未選択 ('') は placeholder に戻す。 */
        fallbackLabel={(v) =>
          v === ''
            ? undefined
            : t(`page:${i18nNs}.form.codeExternalNotFound`, { code: v })
        }
        onBlur={codeField.onBlur}
        onChange={(selectedValue) => {
          if (selectedValue === undefined) {
            setSelectedPartnerId(null)
            codeField.onChange('')
            return
          }
          // 選択された option を composite value で引き直す (= native 時代の
          // `data-code-name` の代替。 引く鍵が composite なので duplicate code でも
          // 選択された account を一意に解決できる)。
          const selected = externalOptsForRow.find(
            (o) => compositeValue(o) === selectedValue,
          )
          if (selected === undefined) return
          setSelectedPartnerId(selected.partnerId)
          codeField.onChange(selected.code)
          if (hideAutoFilledCodeName) {
            codeNameField.onChange(selected.accountCodeName)
          }
        }}
        options={externalOptsForRow.map((o) => ({
          value: compositeValue(o),
          label: o.label,
        }))}
        placeholder={t(`page:${i18nNs}.form.externalCodeUnselected`)}
        value={displayValue}
      />
      {fieldState.error !== undefined && (
        <FieldErrorText>{fieldState.error.message}</FieldErrorText>
      )}
    </FieldRoot>
  )
}
